/* eslint-disable no-unused-vars */
/* eslint-disable max-lines */
import {
 JSX
} from 'react';
import {
 Badge
} from 'shared/components/ui/badge';

// Data type configurations
export const DATA_TYPE_CONFIGS: Record<string, { hasLength: boolean, defaultLength: number }> = {
    string: {
        hasLength: true,
        defaultLength: 255
    },
    integer: {
        hasLength: true,
        defaultLength: 10
    },
    decimal: {
        hasLength: false,
        defaultLength: 10
    },
    boolean: {
        hasLength: false,
        defaultLength: 0
    },
    date: {
        hasLength: false,
        defaultLength: 0
    },
    time: {
        hasLength: false,
        defaultLength: 0
    },
    datetime: {
        hasLength: false,
        defaultLength: 0
    },
    enum: {
        hasLength: false,
        defaultLength: 0
    },
    connection: {
        hasLength: false,
        defaultLength: 0
    },
    list: {
        hasLength: false,
        defaultLength: 0
    },
    equation: {
        hasLength: false,
        defaultLength: 0
    },
};

// Default values for data types
export const DATA_TYPE_DEFAULTS: Record<string, any> = {
    string: '',
    integer: 0,
    decimal: '0.00',
    boolean: false,
    date: null,
    time: null,
    datetime: null,
    enum: '',
    connection: '',
    list: null,
    equation: '',
    validationType: '',
};

// Default new field template
export const DEFAULT_NEW_FIELD: {
    columnName: string;
    dataType: string;
    length: number;
    fieldsAccessBy: string;
    description: string;
    format: string;
    default: any;
    constraints: string[];
    references: { type: string; table: string; column: string };
    values: string[];
    isNew: boolean;
    isModified: boolean;
    equationFormula: string;
    validation: string;
} = {
    columnName: 'New Field',
    dataType: '',
    length: DATA_TYPE_CONFIGS.string.defaultLength,
    fieldsAccessBy: '',
    description: '',
    format: '',
    default: DATA_TYPE_DEFAULTS.string,
    constraints: [],
    references: {
        type: '',
        table: '',
        column: '',
    },
    values: [''],
    isNew: true,
    isModified: false,
    equationFormula: '',
    validation: '',
};

// Column configurations
export const COLUMN_CONFIGS: { id: string, label: string }[] = [
    {
 id: 'columnName',
label: 'Field Name'
},
    {
 id: 'dataType',
label: 'Data Type'
},
    {
 id: 'length',
label: 'Length'
},
    {
 id: 'format',
label: 'Format'
},
    {
 id: 'options',
label: 'Value'
},
    {
 id: 'default',
label: 'Default'
},
    {
 id: 'fieldsAccessBy',
label: 'Field Access'
},
    {
 id: 'constraints',
label: 'Constraints'
},
    {
 id: 'references',
label: 'References'
},
    {
 id: 'equationFormula',
label: 'Equation Formula'
},
    {
 id: 'validation',
label: 'Validation'
},
    {
 id: 'description',
label: 'Description'
},
];

// Data types with colors
export const dataTypes: { value: string; label: string; color: string }[] = [
    {
 value: 'string',
label: 'string',
color: 'bg-blue-100 text-blue-800'
},
    {
 value: 'integer',
label: 'integer',
color: 'bg-green-100 text-green-800'
},
    {
 value: 'decimal',
label: 'decimal',
color: 'bg-yellow-100 text-yellow-800'
},
    {
 value: 'boolean',
label: 'boolean',
color: 'bg-purple-100 text-purple-800'
},
    {
 value: 'date',
label: 'date',
color: 'bg-pink-100 text-pink-800'
},
    {
 value: 'time',
label: 'time',
color: 'bg-indigo-100 text-indigo-800'
},
    {
 value: 'datetime',
label: 'datetime',
color: 'bg-yellow-100 text-yellow-800'
},
    {
 value: 'enum',
label: 'enum',
color: 'bg-orange-100 text-orange-800'
},
    {
 value: 'connection',
label: 'connection',
color: 'bg-orange-100 text-orange-800'
},
    {
 value: 'list',
label: 'list',
color: 'bg-teal-100 text-teal-800'
},
    {
 value: 'equation',
label: 'equation',
color: 'bg-cyan-100 text-cyan-800'
},
];

// Constraint types with colors
export const constraintTypes: { value: string; label: string; color: string }[] = [
    {
 value: 'CASESENSITIVE',
label: 'CASESENSITIVE',
color: 'bg-red-100 text-red-800'
},
    {
 value: 'FOREIGN KEY',
label: 'FOREIGN KEY',
color: 'bg-green-100 text-green-800'
},
    {
 value: 'UNIQUE',
label: 'UNIQUE',
color: 'bg-yellow-100 text-yellow-800'
},
    {
 value: 'INDEX',
label: 'INDEX',
color: 'bg-blue-100 text-blue-800'
},
    {
 value: 'NOT NULL',
label: 'NOT NULL',
color: 'bg-orange-100 text-orange-800'
},
];

// Relationship types
export const relationshipTypes: { value: string; label: string }[] = [
    {
 value: 'one-to-one',
label: 'One-to-One'
},
    {
 value: 'one-to-many',
label: 'one-to-many'
},
];

// Constraint groups
export const constraintGroups = {
    keyConstraints: ['PRIMARY KEY', 'FOREIGN KEY'],
    nullConstraints: ['NOT NULL'],
};

// Handle constraint changes
export const handleConstraintChange = (
    currentConstraints: string[],
    newConstraint: string,
    fieldDataType: string|null
): string[] => {
    let updatedConstraints = [...currentConstraints];
    const isKeyConstraint = constraintGroups.keyConstraints.includes(newConstraint);
    const isNullConstraint = constraintGroups.nullConstraints.includes(newConstraint);

    const isCurrentlySelected = updatedConstraints.includes(newConstraint);

    // Automatically select "FOREIGN KEY" when the data type is "connection"
    if (fieldDataType === 'connection' && !updatedConstraints.includes('FOREIGN KEY')) {
        updatedConstraints.push('FOREIGN KEY');
    }

    // Remove "FOREIGN KEY" if the data type is changed from "connection"
    if (fieldDataType !== 'connection' && updatedConstraints.includes('FOREIGN KEY')) {
        updatedConstraints = updatedConstraints.filter((constraint) => constraint !== 'FOREIGN KEY');
    }

    if (!isCurrentlySelected) {
        // Adding a new constraint — remove conflicting constraints first
        if (isKeyConstraint) {
            updatedConstraints = updatedConstraints.filter(
                (c) => !constraintGroups.keyConstraints.includes(c)
            );
        }
        if (isNullConstraint) {
            updatedConstraints = updatedConstraints.filter(
                (c) => !constraintGroups.nullConstraints.includes(c)
            );
        }
        updatedConstraints.push(newConstraint);
    } else {
        // Removing an existing constraint — just remove it
        updatedConstraints = updatedConstraints.filter((c) => c !== newConstraint);
    }

    return updatedConstraints;
};

// Format field value display for table/grid cells
export const formatFieldValue = (
  value: any,
  fieldType: string,
  field: { dataType: string; validationType: string }
): JSX.Element | string => {
  if (value === null || value === undefined) return '';

  const dataTypeConfig = DATA_TYPE_CONFIGS[field.dataType];

  switch (fieldType) {
    case 'dataType': {
      const dataType = dataTypes.find((dt) => dt.value === value);
      return (
          <Badge variant="outline" className={`${dataType?.color || ''} font-medium`}>
              {dataType?.label || value}
          </Badge>
      );
    }

    case 'length':
      return dataTypeConfig?.hasLength ? String(value || dataTypeConfig.defaultLength) : '0';

    case 'constraints':
      if (!Array.isArray(value) || !value.length) return null;
      return (
          <div className="flex flex-wrap gap-1">
              {value.map((constraint) => {
            const constraintType = constraintTypes.find((ct) => ct.value === constraint);
            return (
                <Badge
                key={constraint}
                variant="outline"
                className={`${constraintType?.color || ''} font-medium`}
              >
                    {constraint}
                </Badge>
            );
          })}
          </div>
      );

    case 'validationType':
      return field.validationType
        ? `Validation: ${field.validationType}`
        : 'No validation';

    case 'fieldsAccessBy':
      if (!Array.isArray(value) || !value.length) return '-';
      return (
          <div className="flex flex-wrap gap-1">
              {value.map((role, idx) => (
                  <Badge key={idx} variant="outline" className="bg-gray-100 text-gray-800">
                      {role}
                  </Badge>
          ))}
          </div>
      );

    case 'references':
      if (!value?.type) return '';
      return (
          <Badge variant="outline" className="bg-green-100 text-green-800">
              {`${value.type} → ${value.table}.${value.column}`}
          </Badge>
      );

    case 'options': {
      // Show all enum options as badges (non-editable display)
      if (field.dataType === 'enum' && Array.isArray(value)) {
        return (
            <div className="flex flex-wrap gap-1">
                {value.map((opt, idx) => (
                    <Badge key={idx} variant="outline" className="bg-gray-100 text-gray-800">
                        {opt}
                    </Badge>
            ))}
            </div>
        );
      }
      // fallback to string display
      return String(value);
    }

    case 'values': {
      // Show selected enum values like fieldsAccessBy badges
      if (field.dataType === 'enum' && Array.isArray(value)) {
        if (value.length === 0) return '-';
        return (
            <div className="flex flex-wrap gap-1">
                {value.map((val, idx) => (
                    <Badge key={idx} variant="outline" className="bg-indigo-100 text-indigo-800">
                        {val}
                    </Badge>
            ))}
            </div>
        );
      }
      return String(value);
    }

    case 'default': {
      switch (field.dataType) {
        case 'boolean':
          return value === true ? 'True' : 'False';
        case 'decimal':
          return parseFloat(value).toFixed(2);
        case 'enum':
          return value || 'Not Selected';
        case 'list': {
          // Display a list as comma-separated values or as badges
          if (Array.isArray(value) && value.length > 0) {
            return (
                <div className="flex flex-wrap gap-1">
                    {value.map((val, idx) => (
                        <Badge key={idx} variant="outline" className="bg-gray-100 text-gray-800">
                            {val}
                        </Badge>
                ))}
                </div>
            );
          }
          return '-'; // for an empty list
        }
        case 'equation': {
          // Display the equation formula or result (if provided)
          return value || '-';
        }
        default:
          return String(value);
      }
    }

    default: {
      if (typeof value === 'object') {
        return Array.isArray(value) ? `${value.length} items` : JSON.stringify(value);
      }
      return String(value);
    }
  }
};
